import type { OrderLogItem, UnpaidOrderItem } from '../interface/clickHouseInterface';
import { isNativePlatform, exportTablePdfNative, exportTableXlsxNative } from './capacitorExport';
import { getCurrencySymbol, formatMoneyForExport } from './currency';

// Format a monetary value for export: whole numbers stay as-is (18 -> "18"),
// fractional values are fixed to exactly 2 decimals (18.9 -> "18.90").
// Use ONLY for monetary columns — never for counts, quantities, or guests.
export function formatMonetaryExport(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return String(value ?? '');
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

// UTF-8 BOM. Prefix every exported CSV with this so Excel decodes the file as
// UTF-8 instead of the system locale (Windows-1252), which would otherwise
// mangle the currency symbol (₹ → "â‚¹"). Required because currency symbols are
// kept verbatim in exports (product rule: the exact active symbol — ₹ / $ / none —
// must appear in the file).
export const CSV_BOM = String.fromCharCode(0xfeff);

// Map common non-ASCII characters to ASCII equivalents. The em-dash "—" is the
// "empty value" placeholder the API/UI uses, and smart quotes/ellipsis come from
// free-text fields. Folding these to ASCII keeps cells robust in every reader.
// Currency symbols are deliberately NOT mapped — the exact active symbol must
// appear in exported files (see CSV_BOM above for how ₹ stays decodable).
const NON_ASCII_MAP: Record<string, string> = {
  '—': '-', '–': '-', '―': '-', '−': '-',           // dashes → hyphen
  '“': '"', '”': '"', '„': '"', '‘': "'", '’': "'", '‚': "'",
  '…': '...', '•': '*', ' ': ' ',               // ellipsis, bullet, nbsp
};

// Fold a string to printable ASCII while PRESERVING currency symbols: map known
// typographic symbols, strip diacritics from accented Latin letters (é → e),
// then drop anything still outside ASCII — except ¢/£/¥ (U+00A2–U+00A5) and the
// Currency Symbols block (U+20A0–U+20BF, includes ₹) which must survive verbatim.
export function foldToAscii(input: string): string {
  let s = input;
  for (const [from, to] of Object.entries(NON_ASCII_MAP)) {
    if (s.includes(from)) s = s.split(from).join(to);
  }
  // Decompose accented letters (é → e + combining accent) and strip the marks (U+0300–U+036F).
  s = s.normalize('NFKD').replace(/[̀-ͯ]/g, '');
  // Drop anything still outside printable ASCII (+ tab/newline), keeping currency symbols.
  return s.replace(/[^\x09\x0A\x0D\x20-\x7E¢-¥₠-₿]/g, '');
}

// Amount cell carries its own currency symbol (see formatMoneyForExport) — the
// header stays currency-agnostic so a merged multi-outlet export doesn't imply
// a single currency for rows that may each be in a different one.
const orderLogCsvHeaders = (_sym: string) => ['Order No', 'Date', 'Time', 'Order Type', 'Customer Name', 'Phone', 'Guests', 'Items', 'Amount'];
const orderLogPdfHeaders = (_sym: string) => ['Order No', 'Date', 'Time', 'Order Type', 'Customer Name', 'Phone', 'Guests', 'Items', 'Amount'];

// Normalise a phone value from the API: treat em-dash, en-dash, blank, or
// whitespace-only as "no phone" and return a plain ASCII hyphen instead.
function normalisePhone(raw: string | null | undefined): string {
  if (!raw) return '-';
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '—' || trimmed === '–' || trimmed === '-') return '-';
  return trimmed;
}

// Normalise guests: return the numeric value as a string, or empty string for
// zero/null/undefined/any non-numeric value the API might return (e.g. '—').
function normaliseGuests(raw: number | undefined): string {
  const n = Number(raw ?? 0);
  return !isNaN(n) && n > 0 ? String(n) : '';
}

function orderLogToCsvRows(orders: OrderLogItem[]): string[][] {
  return orders.map((o) => [
    o.orderNo,
    o.orderDate ?? '-',
    o.orderTime,
    o.orderType,
    o.customerName,
    normalisePhone(o.customerPhone),
    normaliseGuests(o.guests),
    o.itemPreview,
    formatMoneyForExport(o.amount),
  ]);
}

function orderLogToPdfRows(orders: OrderLogItem[]): string[][] {
  return orders.map((o) => [
    o.orderNo,
    o.orderDate ?? '—',
    o.orderTime,
    o.orderType,
    o.customerName,
    o.customerPhone,
    o.guests ? String(o.guests) : '—',
    o.itemPreview,
    formatMoneyForExport(o.amount),
  ]);
}

// Shapes a fetched Order Log page into { headers, rows } for ReportSectionExportButton's
// buildExport contract — used so the Paid Orders export can go through the shared
// Scope (This outlet / All outlets) modal like every other exportable section instead
// of firing a one-shot redux export thunk that bypasses the modal.
export function orderLogToExportTable(orders: OrderLogItem[], currencySymbol: string, format: 'csv' | 'excel' | 'pdf'): { headers: string[]; rows: (string | number)[][] } {
  return format === 'pdf'
    ? { headers: orderLogPdfHeaders(currencySymbol), rows: orderLogToPdfRows(orders) }
    : { headers: orderLogCsvHeaders(currencySymbol), rows: orderLogToCsvRows(orders) };
}

// Same as orderLogToExportTable, for the Unpaid Orders log.
export function unpaidOrdersToExportTable(orders: UnpaidOrderItem[], currencySymbol: string, format: 'csv' | 'excel' | 'pdf'): { headers: string[]; rows: (string | number)[][] } {
  return format === 'pdf'
    ? { headers: unpaidOrdersPdfHeaders(currencySymbol), rows: unpaidOrdersToCsvRows(orders) }
    : { headers: unpaidOrdersCsvHeaders(currencySymbol), rows: unpaidOrdersToCsvRows(orders) };
}

function buildCsv(headers: string[], rows: string[][]): string {
  const escape = (v: string) => {
    // Fold to ASCII first (currency symbols preserved) so typographic chars
    // (em-dash placeholder, smart quotes, accents) are robust in every reader.
    const str = foldToAscii(String(v ?? ''));
    if (str === '') return '""';
    // Wrap EVERY non-empty cell as literal text via the Excel/Sheets ="..."
    // convention. A CSV has no cell styling, so spreadsheet apps auto-detect
    // numeric/date cells and RIGHT-align them, which looks jagged next to the
    // left-aligned text cells — the PDF and XLSX exports left-align every cell,
    // and force-text is the only lever a CSV has to match that. The wrapper also
    // preserves leading zeros ("017869"), trailing decimal zeros ("12.50") and
    // long numeric IDs (else Excel shows scientific notation), and neutralises
    // formula injection (a leading =/+/-/@ can only evaluate to the quoted string).
    // Two escaping layers: Excel-escape the quotes for the formula string, then
    // CSV-escape the whole field, so a value containing a literal " stays intact.
    const field = `="${str.replace(/"/g, '""')}"`;
    return `"${field.replace(/"/g, '""')}"`;
  };
  // BOM so Excel decodes the preserved currency symbol (₹) as UTF-8.
  return `${CSV_BOM}${[headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join('\r\n')}`;
}

function triggerDownloadWeb(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadOrderLogCsv(orders: OrderLogItem[], filename: string, currencySymbol: string = getCurrencySymbol()): Promise<void> {
  // Native saves .xlsx so the currency symbol renders in Google Sheets Android
  // (which decodes CSV as Latin-1) as well as Excel.
  if (isNativePlatform()) {
    await exportTableXlsxNative(orderLogCsvHeaders(currencySymbol), orderLogToCsvRows(orders), filename);
    return;
  }
  const csv = buildCsv(orderLogCsvHeaders(currencySymbol), orderLogToCsvRows(orders));
  triggerDownloadWeb(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

export async function downloadOrderLogExcel(orders: OrderLogItem[], filename: string, currencySymbol: string = getCurrencySymbol()): Promise<void> {
  const headers = orderLogCsvHeaders(currencySymbol);
  const rows = orderLogToCsvRows(orders);
  if (isNativePlatform()) {
    await exportTableXlsxNative(headers, rows, filename, 'Order Log (Paid Orders)');
    return;
  }
  const { buildXlsx, xlsxFilename } = await import('./xlsxExport');
  const bytes = buildXlsx(headers, rows, 'Order Log (Paid Orders)');
  triggerDownloadWeb(
    new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    xlsxFilename(filename),
  );
}

// Amount cell carries its own currency symbol (see formatMoneyForExport) — the
// header stays currency-agnostic, matching the Paid Orders export (orderLogCsvHeaders).
const unpaidOrdersCsvHeaders = (_sym: string) => ['Order ID', 'Date', 'Time', 'Order Type', 'Customer', 'Table No.', 'Guests', 'Server', 'Items', 'Amount'];
const unpaidOrdersPdfHeaders = (_sym: string) => ['Order ID', 'Date', 'Time', 'Order Type', 'Customer', 'Table No.', 'Guests', 'Server', 'Items', 'Amount'];

function unpaidOrdersToCsvRows(orders: UnpaidOrderItem[]): string[][] {
  return orders.map((o) => [
    o.orderNo,
    o.orderDate ?? '-',
    o.orderTime,
    o.orderType,
    o.customerName ?? '-',
    // tableName is a list of every table the check is open on (ORD-1.2); a
    // non-dine-in order sends an empty list.
    (Array.isArray(o.tableName) ? o.tableName.join(', ') : o.tableName) || '-',
    o.guestCount ? String(o.guestCount) : '-',
    o.serverName,
    o.itemPreview ?? '-',
    formatMoneyForExport(o.amount),
  ]);
}

export async function downloadUnpaidOrdersCsv(orders: UnpaidOrderItem[], filename: string, currencySymbol: string = getCurrencySymbol()): Promise<void> {
  // Native saves .xlsx so the currency symbol renders in Google Sheets Android
  // (which decodes CSV as Latin-1) as well as Excel.
  if (isNativePlatform()) {
    await exportTableXlsxNative(unpaidOrdersCsvHeaders(currencySymbol), unpaidOrdersToCsvRows(orders), filename);
    return;
  }
  const csv = buildCsv(unpaidOrdersCsvHeaders(currencySymbol), unpaidOrdersToCsvRows(orders));
  triggerDownloadWeb(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

export async function downloadUnpaidOrdersExcel(orders: UnpaidOrderItem[], filename: string, currencySymbol: string = getCurrencySymbol()): Promise<void> {
  const headers = unpaidOrdersCsvHeaders(currencySymbol);
  const rows = unpaidOrdersToCsvRows(orders);
  if (isNativePlatform()) {
    await exportTableXlsxNative(headers, rows, filename, 'Order Log (Unpaid Orders)');
    return;
  }
  const { buildXlsx, xlsxFilename } = await import('./xlsxExport');
  const bytes = buildXlsx(headers, rows, 'Order Log (Unpaid Orders)');
  triggerDownloadWeb(
    new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    xlsxFilename(filename),
  );
}

export async function downloadUnpaidOrdersPdf(orders: UnpaidOrderItem[], filename: string, currencySymbol: string = getCurrencySymbol()): Promise<void> {
  const exportedOn = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  if (isNativePlatform()) {
    const headers = unpaidOrdersPdfHeaders(currencySymbol);
    const rows = unpaidOrdersToCsvRows(orders);
    await exportTablePdfNative('Order Log (Unpaid Orders)', `Exported on ${exportedOn}`, headers, rows, filename);
    return;
  }
  const rows = unpaidOrdersToCsvRows(orders);
  const thStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#475569;background:#F5F6FA;text-transform:uppercase"';
  const tdStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;font-size:12px;color:#1A1D2E"';
  const ths = unpaidOrdersPdfHeaders(currencySymbol).map((h) => `<th ${thStyle}>${h}</th>`).join('');
  const trs = rows.map((r) => `<tr>${r.map((c) => `<td ${tdStyle}>${String(c ?? '').replace(/</g, '&lt;')}</td>`).join('')}</tr>`).join('');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Order Log (Unpaid Orders)</title>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>body{font-family:'DM Sans',sans-serif;padding:40px;color:#1A1D2E}h1{font-size:18px;font-weight:700;margin-bottom:4px}.sub{font-size:12px;color:#475569;margin-bottom:20px}table{width:100%;border-collapse:collapse;margin-top:8px}.brand{display:flex;align-items:center;gap:10px;margin-bottom:24px}.logo{width:28px;height:28px;border-radius:8px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px}.bname{font-size:14px;font-weight:700}.bsub{font-size:9px;color:#64748B;letter-spacing:0.5px}@media print{body{padding:20px}button{display:none!important}}</style>
  </head><body>
    <div class="brand"><div class="logo">M</div><div><div class="bname">Maghil</div><div class="bsub">ANALYTICS</div></div></div>
    <h1>Order Log (Unpaid Orders)</h1>
    <div class="sub">Exported on ${exportedOn}</div>
    <button onclick="window.print()" style="padding:8px 20px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:12px;font-weight:600;cursor:pointer;margin-bottom:16px">Print / Save as PDF</button>
    <table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>
  </body></html>`;
  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); }
}

export async function downloadOrderLogPdf(orders: OrderLogItem[], filename: string, currencySymbol: string = getCurrencySymbol()): Promise<void> {
  const exportedOn = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  if (isNativePlatform()) {
    const headers = orderLogPdfHeaders(currencySymbol);
    const rows = orderLogToPdfRows(orders);
    await exportTablePdfNative('Order Log (Paid Orders)', `Exported on ${exportedOn}`, headers, rows, filename);
    return;
  }
  const rows = orderLogToPdfRows(orders);
  const thStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;text-align:left;font-size:11px;font-weight:600;color:#475569;background:#F5F6FA;text-transform:uppercase"';
  const tdStyle = 'style="border:1px solid #E2E5EC;padding:8px 12px;font-size:12px;color:#1A1D2E"';
  const ths = orderLogPdfHeaders(currencySymbol).map((h) => `<th ${thStyle}>${h}</th>`).join('');
  const trs = rows.map((r) => `<tr>${r.map((c) => `<td ${tdStyle}>${String(c ?? '').replace(/</g, '&lt;')}</td>`).join('')}</tr>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Order Log (Paid Orders)</title>
    <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>body{font-family:'DM Sans',sans-serif;padding:40px;color:#1A1D2E}h1{font-size:18px;font-weight:700;margin-bottom:4px}.sub{font-size:12px;color:#475569;margin-bottom:20px}table{width:100%;border-collapse:collapse;margin-top:8px}.brand{display:flex;align-items:center;gap:10px;margin-bottom:24px}.logo{width:28px;height:28px;border-radius:8px;background:linear-gradient(135deg,#D23A3B,#E86B6B);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px}.bname{font-size:14px;font-weight:700}.bsub{font-size:9px;color:#64748B;letter-spacing:0.5px}@media print{body{padding:20px}button{display:none!important}}</style>
  </head><body>
    <div class="brand"><div class="logo">M</div><div><div class="bname">Maghil</div><div class="bsub">ANALYTICS</div></div></div>
    <h1>Order Log (Paid Orders)</h1>
    <div class="sub">Exported on ${exportedOn}</div>
    <button onclick="window.print()" style="padding:8px 20px;background:#D23A3B;color:#fff;border:none;border-radius:6px;font-family:'DM Sans';font-size:12px;font-weight:600;cursor:pointer;margin-bottom:16px">Print / Save as PDF</button>
    <table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>
  </body></html>`;

  const win = window.open('', '_blank');
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}
